// タイムラインに出すカードの種類を選ぶための定義
//
// localStorage には「隠している種類」だけを持たせる。こうしておくと後から種類が
// 増えたときに、既存ユーザーの画面でも新しい種類が最初から表示される。
import {
  Book,
  Dumbbell,
  Footprints,
  Image,
  type LucideIcon,
  MapPin,
  Moon,
  NotebookPen,
  ReceiptText,
  Route,
} from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'

export type TimelineItemType =
  | 'visit'
  | 'movement'
  | 'sleep'
  | 'garmin'
  | 'steps'
  | 'read'
  | 'note'
  | 'photos'
  | 'spending'

export type TimelineItemFilterSpec = {
  value: TimelineItemType
  label: string
  icon: LucideIcon
  color: string
}

export const TIMELINE_ITEM_FILTERS: readonly TimelineItemFilterSpec[] = [
  { value: 'visit', label: '訪問', icon: MapPin, color: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' },
  { value: 'movement', label: '移動', icon: Route, color: 'bg-sky-500/15 text-sky-400 border-sky-500/30' },
  { value: 'sleep', label: '睡眠', icon: Moon, color: 'bg-indigo-500/15 text-indigo-400 border-indigo-500/30' },
  { value: 'garmin', label: '運動', icon: Dumbbell, color: 'bg-blue-500/15 text-blue-400 border-blue-500/30' },
  { value: 'steps', label: '歩数', icon: Footprints, color: 'bg-teal-500/15 text-teal-400 border-teal-500/30' },
  { value: 'read', label: '読書', icon: Book, color: 'bg-violet-500/15 text-violet-400 border-violet-500/30' },
  { value: 'note', label: 'メモ', icon: NotebookPen, color: 'bg-amber-500/15 text-amber-400 border-amber-500/30' },
  { value: 'photos', label: '写真', icon: Image, color: 'bg-pink-500/15 text-pink-400 border-pink-500/30' },
  {
    value: 'spending',
    label: '買い物',
    icon: ReceiptText,
    color: 'bg-orange-500/15 text-orange-400 border-orange-500/30',
  },
]

/** 日付ごとビューで扱う種類（全部） */
export const DAILY_TIMELINE_TYPES: readonly TimelineItemType[] = TIMELINE_ITEM_FILTERS.map((filter) => filter.value)

/** 無限スクロールビューで扱う種類（メモと写真は出していない） */
export const INFINITE_TIMELINE_TYPES: readonly TimelineItemType[] = [
  'visit',
  'movement',
  'sleep',
  'garmin',
  'steps',
  'read',
  'spending',
]

const STORAGE_KEY = 'activity-timeline-hidden-types'

const isTimelineItemType = (value: unknown): value is TimelineItemType =>
  typeof value === 'string' && TIMELINE_ITEM_FILTERS.some((filter) => filter.value === value)

const readHiddenTypes = (): Set<TimelineItemType> => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return new Set()
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return new Set()
    return new Set(parsed.filter(isTimelineItemType))
  } catch {
    return new Set()
  }
}

export type TimelineItemFilter = {
  hiddenTypes: ReadonlySet<TimelineItemType>
  isVisible: (type: TimelineItemType) => boolean
  toggleType: (type: TimelineItemType) => void
}

/** 初期値は全表示。選んだ状態は localStorage に残す。 */
export const useTimelineItemFilter = (): TimelineItemFilter => {
  const [hiddenTypes, setHiddenTypes] = useState<Set<TimelineItemType>>(readHiddenTypes)

  const toggleType = useCallback(
    (type: TimelineItemType) => {
      const next = new Set(hiddenTypes)
      if (next.has(type)) next.delete(type)
      else next.add(type)
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...next]))
      setHiddenTypes(next)
    },
    [hiddenTypes],
  )

  const isVisible = useCallback((type: TimelineItemType) => !hiddenTypes.has(type), [hiddenTypes])

  return useMemo(() => ({ hiddenTypes, isVisible, toggleType }), [hiddenTypes, isVisible, toggleType])
}
